/**
 * Two small, additive behaviors layered on top of Shopify's native
 * predictive-search-component (assets/predictive-search.js) inside
 * Furlouve's branded search modal. Both run as independent listeners
 * alongside Horizon's own — neither touches predictive-search.js itself,
 * so it stays untouched for future theme resyncs.
 *
 * 1. Min-length gate: visually hides the results dropdown until 2+
 *    characters are typed (the fetch itself still fires natively at 1
 *    character — harmless, just suppressed here client-side).
 * 2. Highlight-on-morph: wraps the matched substring of each rendered
 *    product title in <mark>, re-run via MutationObserver since morph()
 *    doesn't expose a completion callback externally.
 */

const MIN_LENGTH = 2;

const searchRoot = document.querySelector('.flv-search-modal predictive-search-component');

if (searchRoot) {
  const searchInput = /** @type {HTMLInputElement | null} */ (searchRoot.querySelector('[ref="searchInput"]'));
  const resultsWrapper = searchRoot.querySelector('.flv-search-modal__results');

  if (searchInput) {
    const updateMinLengthState = () => {
      const length = searchInput.value.trim().length;
      const isBelowMinLength = length > 0 && length < MIN_LENGTH;
      searchRoot.classList.toggle('is-below-min-length', isBelowMinLength);
    };

    searchInput.addEventListener('input', updateMinLengthState);
    updateMinLengthState();
  }

  if (resultsWrapper) {
    /**
     * Wraps the current search term inside each rendered product title.
     * @param {string} term
     */
    const highlightTitles = (term) => {
      if (!term) return;

      const lowerTerm = term.toLowerCase();

      resultsWrapper.querySelectorAll('[data-search-title]').forEach((titleEl) => {
        const text = titleEl.textContent ?? '';
        const index = text.toLowerCase().indexOf(lowerTerm);

        if (index === -1) {
          titleEl.textContent = text;
          return;
        }

        const before = text.slice(0, index);
        const match = text.slice(index, index + term.length);
        const after = text.slice(index + term.length);

        const mark = document.createElement('mark');
        mark.textContent = match;

        titleEl.replaceChildren(document.createTextNode(before), mark, document.createTextNode(after));
      });
    };

    const observer = new MutationObserver(() => {
      // Disconnect while we mutate so wrapping matches in <mark> doesn't
      // trigger the observer against its own changes.
      observer.disconnect();
      highlightTitles(searchInput?.value.trim() ?? '');
      observer.observe(resultsWrapper, { childList: true, subtree: true });
    });

    observer.observe(resultsWrapper, { childList: true, subtree: true });
  }
}
